<script>
import Layout from '@layouts/main'
import LayoutMemberPrepaid from '@layouts/LayoutMemberPrepaid'
import { merchantComputed } from '@state/helpers'
import formatDate, { dateDiffInMinutes } from '@utils/format-date'
import { nlToBr } from '@utils'

export default {
  page() {
    return {
      title: this.$t('global.MEMBER_PREPAID'/* 預付方案 */),
    }
  },
  components: { Layout, LayoutMemberPrepaid },
  filters: {
    formatDate,
    nlToBr,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultImage: require('@assets/images/activity_default.png'),
    }
  },
  computed: {
    ...merchantComputed,
    orderTimeInterval() {
      return dateDiffInMinutes(this.order.startDate, this.order.endDate)
    },
    paramsSizeTotal() {
      let size = 0;

      this.order.params.forEach(param => {
        size += param.size * param.qty
      });

      return size
    },
    gender() {
      const gender = {
        male: this.$t('global.MALE' /* 先生 */),
        female: this.$t('global.FEMALE' /* 小姐 */),
      };
      return gender[this.order.gender]
    },
    paymentMethod() {
      const paymentMethod = {
        prepaid: this.$t('global.PAYMENT_METHOD_PREPAID' /* 預付抵用 */),
        cash: this.$t('global.PAYMENT_METHOD_CASH' /* 現金付款 */),
        creditCard: this.$t('global.PAYMENT_METHOD_CREDITCARD' /* 信用卡付款 */),
        free: this.$t('global.PAYMENT_METHOD_FREE' /* 免費 */),
      };
      return paymentMethod[this.order.paymentMethod]
    },
    isNullOrder() {
      return typeof this.order === 'object' && Object.keys(this.order).length === 0
    }
  },
  methods: {
    orderStatusText(orderStatus) {
      const status = [
        this.$t('global.CANCELED' /* 已取消 */),
        this.$t('global.PAID' /* 已付款 */),
        this.$t('global.UNPAID' /* 未付款 */),
      ];
      return status[orderStatus]
    },
    orderStatusClass(orderStatus) {
      const statusClassMap = {
        0: 'is-orange',
        1: 'is-primary',
        2: 'is-danger',
      };
      return statusClassMap[orderStatus]
    },
    applicableItem(applicable) {
      return this.order.prepaid.applicableEvents[applicable.title].join(",")
    }
  },
}
</script>

<template>
  <Layout>
    <LayoutMemberPrepaid :order="order">
      <template v-if="!isNullOrder">
        <div :class="$style.wrapper">
          <div :class="$style.inner" class="is-mobile-column-reverse">
            <div class="columns is-multiline is-mobile is-mobile-column-reverse">
              <div class="column is-full-mobile is-half-tablet">
                <div class="media is-align-items-stretch">
                  <figure class="media-left">
                    <picture>
                      <BaseImage
                        :src="order.prepaid.introimage"
                        :error-src="defaultImage"
                        :class="$style.image"
                        class-name="is-cover"
                      />
                    </picture>
                  </figure>
                  <div class="media-content">
                    <div :class="$style.titleBox" class="level">
                      <div :class="$style.title" class="level-left">
                        <b-tag :type="orderStatusClass(order.status)" rounded style="margin-right: 0.5rem">
                          {{orderStatusText(order.status) }}
                        </b-tag> {{ order.prepaid.title }}
                      </div>
                    </div>
                    <div class="has-text-grey-light is-text-content">
                      {{ order.merchant.name }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="column is-full-mobile is-half-tablet">
                <div>
                  <div class="level" style="margin-bottom: 0">
                    <div class="level-left"></div>
                    <div class="level-right">
                      <div class="has-text-grey-light" style="margin-bottom: 1rem">{{ $t('global.ORDER_NO'/*訂單編號*/) }} #{{order.orderNo}}</div>
                    </div>
                  </div>
                  <div class="level">
                    <div class="level-left"></div>
                    <div class="level-right">
                      <div v-if="order.status !== 2"
                           :class="[$style.styleBlockItem, { [$style.isDisabled]: order.status === 0 }]">
                        <div class="level is-mobile">
                          <div class="level-left">
                            <span class="has-text-grey-light" style="margin-right: 0.5rem">{{ $t('global.REMAINDER_DEDUCT'/*剩餘抵用次數*/) }}</span>
                          </div>
                          <div class="level-right">
                           <span :class="order.status === 0 ? 'has-text-grey-light' : 'has-text-primary'"
                                 class="is-size-4">
                              {{order.times - order.used}}/{{order.times}}
                           </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div :class="$style.inner">
            <div>
              <div class="columns is-multiline is-mobile" :class="$style.applicableProductList">
                <div
                  v-for="(applicableProductItem, index) in order.applicableProducts"
                  :key="index"
                  class="column is-full-mobile is-one-third-tablet has-block-link"
                  :class="$style.applicableProductItem"
                >
                  <router-link
                    class="is-block-link"
                    :to="{
                  name: 'booking-product',
                  params: {
                    merchantAlias: order.merchant.alias,
                    productId: applicableProductItem.alias,
                  },
                  query: {
                    language: $route.query.language,
                  },
                }"
                  ></router-link>
                  <picture>
                    <router-link
                      :to="{
                        name: 'booking-product',
                        params: {
                          merchantAlias: order.merchant.alias,
                          productId: applicableProductItem.alias,
                        },
                        query: {
                          language: $route.query.language,
                        },
                      }"
                    >
                      <BaseImage
                        :src="applicableProductItem.introimage"
                        :error-src="defaultImage"
                        :class="$style.applicableProductItem__image"
                        class-name="is-cover"
                      />
                    </router-link>
                  </picture>
                  <h4 :class="$style.applicableProductItem__title">{{applicableProductItem.title}}</h4>
                  <div :class="$style.applicableProductItem__desc" class="has-text-grey">
                    {{ $t('global.PREPAID_APPLICABLE'/*適用項目*/) }}:{{applicableItem(applicableProductItem)}}
                  </div>
                  <router-link
                    class="button is-primary is-fullwidth is-outlined is-rounded"
                    :to="{
                    name: 'booking-product',
                    params: {
                      merchantAlias: order.merchant.alias,
                      productId: applicableProductItem.alias,
                    },
                    query: {
                      language: $route.query.language,
                    },
                  }"
                  >
                    {{ $t('global.APPLICABLE_BOOKING'/*我要預訂*/) }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </LayoutMemberPrepaid>
  </Layout>
</template>
<style lang="scss" module>
  .wrapper {
    &:not(:last-child) {
      .inner {
        border-bottom: 1px solid $color-border-base;
      }
    }
    .inner:not(:last-child) {
      border-bottom: 1px solid $color-border-base;
    }

    .inner {
      padding: 1rem 0 0.9rem;
      margin: 0 1.5rem;
    }
  }

  .titleBox {
    align-items: flex-start;
    margin-bottom: 1rem;
    .title {
      max-width: 388px;
      align-items: flex-start;
    }
  }

  .styleBlockItem {
    border: 1px solid $color-primary;
    border-radius: 5px;
    padding: 0.75rem 1rem;
    text-align: left;
    &.isDisabled {
      border: 1px solid #b5b5b5;
    }
  }

  .image {
    height: 100px;
    width: 150px;
    overflow: hidden;
    background: $grey-lighter no-repeat;
    border-radius: $size-image-border-radius;
    position: relative;
    background-size: cover;
    @include mobile {
      width: 100px;
      height: 70px;
    }
  }
  .hr {
    border-bottom: 1px dashed $color-border-base;
    margin: 1rem 0;
    background: transparent;
  }

  .applicableProductList {
    .applicableProductItem {
      cursor: pointer;
      margin-bottom: 1rem;
      &__image {
        width: 100%;
        height: 170px;
        margin-bottom: 0.5rem;
        overflow: hidden;
        background: #dbdbdb no-repeat;
        border-radius: 6px;
        position: relative;
        background-size: cover;
        @include mobile {
          width: 328px;
          height: 210px;
        }
      }
      &__title {
        @extend %typography-medium;
        margin-bottom: 0.5rem;
      }
      &__desc {
        margin-bottom: 0.5rem;
        height: 4.2em;
        text-overflow: ellipsis;
        display: -webkit-box;
        overflow: hidden;
        -webkit-line-clamp: 3;
        /* autoprefixer: off */
        -webkit-box-orient: vertical;
        /* autoprefixer: on */
      }
    }
  }
</style>
